// NOKE 收銀 — mobile-first point-of-sale for the 忠泰樂生活 pop-up counter.
// Staff scan barcode price tags with the phone camera (Code 128) to build a
// ticket, read the 應收總額 + Metanoia/MdoC split, and key those into the MALL POS.
// There is NO payment here — the mall POS takes payment and issues the 發票.
// 完成銷售 records the sale in our system (for the vendor split / daily close-out)
// and, optionally, links a 會員 phone (informational only — NO VIP discount at the
// counter, price-parity policy).
//
// TWO TABS, TWO LIFECYCLES — this is a correctness boundary, not a layout choice.
// 收銀 (checkout) and 試衣 (fitting/styling service) are different moments that
// interleave on the shop floor and routinely involve DIFFERENT PEOPLE at the same
// time: one part-timer can be mid-fitting with customer A (body entered, 合身清單
// on screen) when customer B walks up to pay. They therefore have INDEPENDENT
// lifecycles on one page:
//   - Completing a sale clears the TICKET ONLY (resetTicket). It must never touch
//     the 顧客卡, the session, or the 合身清單 — that bug made staff re-ask a
//     customer their 身高體重 to their face.
//   - The customer context is cleared ONLY by an explicit 換顧客 (resetCustomer).
// They stay in ONE component so a 合身清單 row can drop straight into the ticket
// without a context provider or a shared store.
//
// The 會員手機 is deliberately kept twice — 顧客卡 (試衣) binds the person being
// fitted; 收銀 binds the person paying. One shared field would silently stamp
// customer A's phone onto customer B's sale. 收銀 offers a one-tap 帶入 when the
// two are in fact the same person.
//
// 掛單: the 收銀 ticket can be parked whole (lines, discount, 結帳會員,
// client_ref) when its customer goes back to the fitting room, so the next person
// can pay first; 叫回 restores it. See ParkedTicket.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
  Alert,
  Tooltip,
  Collapse,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { useSnackbar } from 'notistack';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import api from '../services/api';
import { bucketVendorTotals, allocateVendorTotals } from '../utils/nokeVendor';
import { pdpUrl } from '../utils/nokePdp';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import NokeManualTab from '../components/noke/NokeManualTab';
import TryonPanel, { TryonPanelHandle } from '../components/noke/TryonPanel';
import { useAuth } from '../contexts/AuthContext';
import timeClockService, { StaffOption } from '../services/timeClockService';

const SCANNER_ELEMENT_ID = 'noke-pos-scanner';
const SALESPERSON_STORAGE_KEY = 'nokePosSalesperson';
// Remembers whether the staff member last had the CAMERA scanner on. A hardware
// (HID keyboard-wedge) scanner is the primary input, so the camera is opt-in
// (default OFF) and only auto-starts if it was left on last time.
const CAMERA_PREF_STORAGE_KEY = 'nokePosCameraOn';

// ---- Types (mirror the /api/noke contract) --------------------------------

interface LookupItem {
  variant_id: number;
  sku: string;
  product_title: string;
  vendor: string;
  color: string;
  size: string;
  price: number;
  qty_on_hand: number;
  qty_office?: number;
  qty_in_transit?: number;
  // Storefront handle — feeds 複製商品連結. Older backends omit it, so every
  // consumer must cope with it being absent rather than printing "undefined"
  // into a link a customer receives.
  handle?: string | null;
  image_url?: string | null;
}

interface TicketLine extends LookupItem {
  qty: number;
  unit_price: number;
  // 贈品 (e.g. the 滿2,000 贈滾珠香水 giveaway): priced 0, still leaves stock.
  is_gift: boolean;
}

// A ticket line's identity. The same variant can legitimately appear twice —
// once sold, once given away — so the key carries the gift flag.
const lineKey = (variantId: number, isGift: boolean) => `${variantId}-${isGift ? 'g' : 'p'}`;

// ---- 活動折扣 (server-priced) ----------------------------------------------

interface QuoteLine {
  variant_id: number;
  qty: number;
  unit_price: number;
  is_gift?: boolean;
  line_discount: number;
  campaign_id: number | null;
  campaign_name: string | null;
}

interface Quote {
  lines: QuoteLine[];
  gross: number;
  campaign_discount: number;
  manual_discount: number;
  discount: number;
  net_total: number;
  vendor_totals: Record<string, number>;
  applied_campaigns: { id: number; name: string; amount: number }[];
  gifts: { id: number; name: string; label: string; min_subtotal: number }[];
}

interface ActiveCampaign {
  id: number;
  name: string;
  kind: string;
  scope: string;
  pay_percent: number | null;
  min_subtotal: number | null;
  gift_label: string | null;
}

interface Customer {
  id: number;
  name: string;
  phone: string;
  is_vip: boolean;
  credit_balance: number;
}

// ---- 顧客卡 / 合身 (POST /session, POST /fit-scan, GET /product-card) --------

// EVERY measurement in this block is a FLAT (half) value in cm — the way our
// size charts are written. 腰 means 腰寬, never 腰圍. Anything rendering these
// numbers must carry a 寬 label; printing a flat number next to a 圍 label
// understates the garment by half and sends a customer home with the wrong size.
interface BodyMeasure {
  shoulder: number | null;
  chest: number | null;
  waist: number | null;
  hip: number | null;
  thigh: number | null;
}

type BuildCode = 'slim' | 'standard' | 'athletic' | 'round';

// Self-description the customer picks — never a verdict staff pronounce.
const BUILD_OPTIONS: { code: BuildCode; label: string }[] = [
  { code: 'slim', label: '偏瘦' },
  { code: 'standard', label: '標準' },
  { code: 'athletic', label: '精壯' },
  { code: 'round', label: '厚實' },
];

interface SessionResponse {
  session_id: string;
  body: BodyMeasure;
  customer: { id: number; name: string; is_vip: boolean } | null;
  profile_saved: boolean;
}

interface FitItem {
  variant_id: number;
  sku: string;
  product_title: string;
  color: string;
  size_in_store: string;
  price: number;
  recommended_size: string | null;
  fit_label: string | null;
  score: number;
  // Superset flag: true for a counter-only elastic degradation AND for a
  // measured elastic waist with a synthesized upper bound. Both are genuinely
  // elastic waistbands, so both get the same 「鬆緊帶，建議試穿」 caption — this
  // is deliberately NOT split into two messages.
  waist_estimated: boolean;
  image_url: string | null;
  handle: string | null;
  vendor: string;
}

// A garment on the rail we could not score. NOT an error and NOT a bad fit —
// "we haven't measured it" and "it doesn't fit you" need opposite reactions from
// a staff member, so these are listed (and addable) rather than hidden. Three of
// the five on the real counter set are real garments with a real size on the tag.
type UnmeasuredReason = 'no_measurements' | 'size_not_in_chart' | 'not_a_garment';

interface UnmeasuredItem {
  variant_id: number;
  sku: string;
  product_title: string;
  color: string;
  size_in_store: string;
  price: number;
  handle: string | null;
  image_url: string | null;
  vendor: string;
  reason: UnmeasuredReason | string;
}

// 'not_a_garment' is absent on purpose — those rows are filtered out before
// render (a BB stick listed as "clothing we could not size" is pure noise).
const UNMEASURED_REASON_LABELS: Record<string, string> = {
  no_measurements: '尚未建檔尺寸',
  size_not_in_chart: '尺寸表沒有這個尺寸',
};

const unmeasuredReasonLabel = (reason: string): string =>
  UNMEASURED_REASON_LABELS[reason] || '尚未建檔尺寸';

const isGarment = (u: UnmeasuredItem): boolean => u.reason !== 'not_a_garment';

// The common shape of a tappable rail row — a scored FitItem and an
// UnmeasuredItem are structurally the same thing to the ticket.
interface ScanRow {
  variant_id: number;
  sku: string;
  product_title: string;
  color: string;
  size_in_store: string;
  price: number;
  vendor?: string;
  handle?: string | null;
  image_url?: string | null;
}

interface FitScanResponse {
  body: BodyMeasure;
  items: FitItem[];
  unmeasured?: UnmeasuredItem[];
  skipped_no_measurements: number;
  skipped_not_a_garment?: number;
}

interface SizeChartRow {
  size_label: string;
  [key: string]: string | number | null;
}

interface ProductCard {
  title: string | null;
  sku: string | null;
  color: string | null;
  size: string | null;
  origin: string | null;
  price: number | null;
  fabric: string | null;
  model_spec: string | null;
  care: string | null;
  size_chart: SizeChartRow[] | null;
  handle: string | null;
  recommended_size: string | null;
}

// What the 商品答案卡 shows while the real card is still loading (or when the
// backend is unreachable) — taken from the row the staffer tapped.
interface SheetFallback {
  title: string;
  color: string;
  size: string;
}

// Size-chart column order. 肩寬 first, then 胸寬, then everything else in the
// order the row hands them over — a house convention, not a cosmetic choice.
const CHART_COLUMN_PRIORITY = ['shoulder_cm', 'chest_cm'];

// Keyed on the column STEM (the name with any `_cm` suffix removed), so a raw
// English header can never reach the counter — staff read this table aloud.
// The authoritative column set is SIZE_CHART_COLS in the backend's
// noke-fit.service.js: shoulder, chest, length, sleeve, cuff, armhole, waist,
// waist_max, hip, thigh, hem, rise. Every one of them is covered below.
const CHART_STEM_LABELS: Record<string, string> = {
  shoulder: '肩寬',
  chest: '胸寬',
  bust: '胸寬',
  waist: '腰寬',
  hip: '臀寬',
  thigh: '大腿',
  cuff: '袖口',
  hem: '褲口',
  rise: '前檔',
  // 腋寬, not 袖襱: this is the word Metanoia's own charts already print. The
  // parser that fills product_measurements maps 腋寬 -> armhole_cm
  // (scripts/storefront/backfill_product_measurements.py:106, 臂圍 aliased at
  // :107), so staff reading the POS and the customer reading the PDP size chart
  // on their phone see the same label.
  armhole: '腋寬',
  length: '衣長',
  top_length: '衣長',
  sleeve: '袖長',
  sleeve_length: '袖長',
  pants_length: '褲長',
  pant_length: '褲長',
  inseam: '內長',
  outseam: '外長',
  // The ONLY 圍 labels in this map, and they exist so a circumference column
  // can never be printed under a 寬 header — that would show the garment at
  // half its real girth. Flat and circumference must never share a label.
  waist_circ: '腰圍',
  chest_circ: '胸圍',
  hip_circ: '臀圍',
};

// `waist_max_cm` → 腰寬上限: the top of an elastic stretch range, not a separate
// measurement, and it must sit next to 腰寬 reading as its upper bound (the house
// shorthand for the same thing is 腰寬30~). Derived rather than hardcoded so any
// future `*_max_cm` / `*_min_cm` column inherits a Chinese header for free.
const chartColumnLabel = (key: string): string => {
  const stem = key.replace(/_cm$/, '');
  if (CHART_STEM_LABELS[stem]) return CHART_STEM_LABELS[stem];
  const bound = stem.match(/^(.*)_(max|min)$/);
  if (bound && CHART_STEM_LABELS[bound[1]]) {
    return `${CHART_STEM_LABELS[bound[1]]}${bound[2] === 'max' ? '上限' : '下限'}`;
  }
  return stem.replace(/_/g, ' ');
};

// Column keys for a size chart, in house order, derived from the rows actually
// returned (a shirt chart and a trouser chart do not share columns).
const chartColumns = (rows: SizeChartRow[]): string[] => {
  const seen: string[] = [];
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((k) => {
      if (k === 'size_label') return;
      if (row[k] === null || row[k] === undefined || row[k] === '') return;
      if (!seen.includes(k)) seen.push(k);
    });
  });
  const priority = CHART_COLUMN_PRIORITY.filter((k) => seen.includes(k));
  return [...priority, ...seen.filter((k) => !priority.includes(k))];
};

interface CompletedSale {
  id: number;
  sale_ref: string;
  gross: number;
  discount: number;
  campaign_discount?: number;
  manual_discount?: number;
  applied_campaigns?: { id: number; name: string; amount: number }[];
  net_total: number;
  vendor_totals: Record<string, number>;
  customer_id: number | null;
  customer_name: string | null;
  sold_at: string;
  items: any[];
}

// 補貨 hint returned alongside a completed sale: a variant that still has stock
// at the counter after this sale, and where it sits. The piece that just left
// was the one on display — the sale already marked the variant 未上架, so this
// list is a to-do, and 補好了 is what clears it.
// storage_slot is nullable: a variant with no recorded 櫃位 still needs fetching.
interface RestockHint {
  variant_id: number;
  sku: string;
  product_title: string | null;
  color: string | null;
  size: string | null;
  storage_slot: string | null;
  qty_on_hand: number;
}

// ---- Helpers --------------------------------------------------------------

// The API returns createError failures as {error:{message}} (server.js error
// handler) and middleware rejections as a bare {message}. Read both so the real
// reason reaches the person at the counter.
const apiMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error?.message || err?.response?.data?.message || fallback;

const formatNT = (amount: number): string =>
  `NT$ ${Math.round(amount || 0).toLocaleString('en-US')}`;

// ---- 現金找零 ---------------------------------------------------------------
// The counter takes no money of its own — the mall POS does that and issues the
// 發票 — but the staffer still counts change out of the float by hand, and doing
// that on a phone calculator with a queue waiting is where both the mistakes and
// the dead air come from. All of this is arithmetic on the total already on the
// screen; nothing here is sent to the server or recorded on the sale.
const TWD_DENOMS = [1000, 500, 100, 50, 10, 5, 1];

// The amounts a customer actually hands over: the exact total first, then the
// round numbers that come out of a wallet. Deduped and never below the total, so
// a NT$1,000 ticket does not offer 「1,000」 four times.
const cashSuggestions = (total: number): number[] => {
  const t = Math.round(total);
  if (t <= 0) return [];
  const ceilTo = (step: number) => Math.ceil(t / step) * step;
  const raw = [t, ceilTo(100), ceilTo(500), ceilTo(1000), ceilTo(1000) + 1000];
  return Array.from(new Set(raw))
    .filter((v) => v >= t)
    .sort((a, b) => a - b)
    .slice(0, 4);
};

// 找零 split into the notes and coins to hand back. The staffer reads what to
// pull out of the float instead of doing a second sum in their head.
const changeBreakdown = (change: number): { denom: number; count: number }[] => {
  let rest = Math.round(change);
  const out: { denom: number; count: number }[] = [];
  for (const denom of TWD_DENOMS) {
    const count = Math.floor(rest / denom);
    if (count > 0) {
      out.push({ denom, count });
      rest -= count * denom;
    }
  }
  return out;
};

// A field the backend had no data for renders as an em dash. Never invent a
// filler value here — a missing 材質 is information; a made-up one is a lie told
// to a customer's face across the counter.
const orDash = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  return s === '' ? '—' : s;
};

// Small square product thumbnail (same idiom as NokeCounterPage's Thumb).
const PosThumb: React.FC<{ src?: string | null; alt?: string; size?: number }> = ({
  src,
  alt,
  size = 44,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid #e0e0e0',
    }}
  >
    {src ? (
      <img src={src} alt={alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      <ImageOutlinedIcon fontSize="small" sx={{ color: 'grey.400' }} />
    )}
  </Box>
);

// Estimated body, rendered as FLAT widths. The 平量 caption is not decoration —
// it is the only thing stopping someone reading 腰 39 as a 78cm waist.
const BodyRow: React.FC<{ body: BodyMeasure }> = ({ body }) => {
  const cells: { label: string; value: number | null }[] = [
    { label: '肩寬', value: body.shoulder },
    { label: '胸寬', value: body.chest },
    { label: '腰寬', value: body.waist },
    { label: '臀寬', value: body.hip },
    { label: '大腿', value: body.thigh },
  ];
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        推估身型（平量單邊寬度，非圍度）
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
        {cells.map((c) => (
          <Box key={c.label} sx={{ minWidth: 52 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {c.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {c.value === null || c.value === undefined ? '—' : `${c.value} cm`}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// 合身 chip colour. The backend owns the wording (合身 / 偏鬆 / 偏緊); this only
// decides how loud it looks. 穿不下 / 太大 class labels normally never reach the
// list (they are filtered out), but the never-empty fallback shows them on
// purpose — they must read as a warning there, not as a neutral tag.
const fitChipColor = (label: string | null): 'success' | 'warning' | 'error' | 'default' => {
  if (!label) return 'default';
  if (UNWEARABLE_FIT_LABELS.has(label)) return 'error';
  if (label.includes('合身')) return 'success';
  if (label.includes('鬆') || label.includes('緊')) return 'warning';
  return 'default';
};

// ---------------------------------------------------------------------------
// 店內合身清單 — one row per PRODUCT (Magnus, on the real counter set)
// ---------------------------------------------------------------------------
// /fit-scan returns one entry per VARIANT, so a 3-colour × 3-size product ate
// 5 of the 9 visible rows (SKU 2217 後鬆緊落地西裝褲, body 165/60) and buried
// every other garment. The staffer needs the PRODUCT once, with the colours
// they can actually hand over. Three rules, all applied here in the UI — the
// backend response shape is untouched:
//
//   1. Collapse variants to one group per product.
//   2. Per colour keep ONE size: the 建議尺寸 when the store has it, otherwise
//      that colour's best in-store size. A colour whose only in-store size is
//      off-recommendation is NOT dropped — it gets its own honest line
//      (「店內只有 M」), because dropping it hides a wearable garment (深米色
//      of 2217 exists only in M and M genuinely fits that body).
//   3. Hide what we KNOW fits badly (below). Never hide what we merely have not
//      measured — that is the 尚未建檔尺寸 list and it stays unfiltered.

// The recommender's own unwearable set (fit-recommendation.service.js:941).
// fit_label is the DOMINANT measurement's band label — 胸寬 for tops, 腰寬 for
// bottoms — so these are garments whose primary dimension already failed.
const UNWEARABLE_FIT_LABELS = new Set(['穿不下', '太緊', '太窄', '太大', '很鬆', '太鬆']);

// Score floor for "worth showing a customer". `score` is the weighted mean of
// the per-measurement band scores, and the band tables (fit-recommendation
// .service.js:292-354) put every still-wearable hedge word at 0.55 or above
// (偏緊 0.55, 偏長 0.55, 寬鬆 0.8-0.9, 合身 1.0) while everything the engine
// calls a miss sits at or below 0.5 (偏短 0.5, 很鬆 0.4, 太大 0.35, 太緊 0.15,
// 穿不下 0.0). 0.55 is therefore the engine's own wearable/not-wearable line,
// not a number picked to hit a row count. On the live counter set (91 variants
// / 60 products) it keeps 20 products for 180/60 and 24 for 165/60.
const POOR_FIT_SCORE = 0.55;

const worthShowing = (item: FitItem): boolean =>
  item.score >= POOR_FIT_SCORE &&
  !(item.fit_label != null && UNWEARABLE_FIT_LABELS.has(item.fit_label));

// Size labels are free text off the hang tag ("M", " m ", "FREE") — compare
// them the same way the backend's `norm` does before calling two sizes equal.
const sameSize = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const na = String(a ?? '').trim().toUpperCase();
  const nb = String(b ?? '').trim().toUpperCase();
  return na !== '' && na === nb;
};

// SKU is shared by every variant of a product (workflow rule: one SKU per
// product, never per variant); handle/title disambiguate the rare blank SKU.
const productKeyOf = (i: { sku: string; handle?: string | null; product_title: string }): string =>
  `${i.sku ?? ''}::${i.handle ?? i.product_title ?? ''}`;

// One tappable colour inside a product group. `row` is what reaches the ticket
// and the 試穿 sheet — always a SPECIFIC variant, never a product-level guess.
interface ColorOption {
  row: ScanRow;
  color: string;
  size_in_store: string;
  fit_label: string | null;
  waist_estimated: boolean;
  // null for an unmeasured row — it has no fit verdict at all, which is the
  // whole point of keeping that list separate.
  score: number | null;
  // Rendered verbatim under the colour chip. Never two bare letters: 店內 and
  // 建議 are different statements and staff were guessing which to hand over.
  caption: string;
}

interface ProductGroup {
  key: string;
  title: string;
  price: number;
  image_url: string | null;
  recommended_size: string | null;
  options: ColorOption[];
  best_score: number;
}

const fitOptionCaption = (item: FitItem): string => {
  const parts: string[] = [];
  if (item.recommended_size && !sameSize(item.size_in_store, item.recommended_size)) {
    // 可穿 is a claim, so it is only made about a row that cleared the fit gate.
    // In the never-empty fallback below nothing has, and the chip already says
    // 太緊 / 穿不下 — appending 可穿 there would contradict it to a customer.
    parts.push(
      `店內只有 ${orDash(item.size_in_store)}，不是建議的 ${item.recommended_size}${
        worthShowing(item) ? '，可穿' : ''
      }`
    );
  } else if (item.recommended_size) {
    parts.push(`店內 ${orDash(item.size_in_store)}，正是建議尺寸`);
  } else {
    parts.push(`店內 ${orDash(item.size_in_store)}`);
  }
  // Elastic waists are normal for these garments — a caption, not a warning.
  if (item.waist_estimated) parts.push('鬆緊帶，建議試穿');
  return parts.join(' · ');
};

/**
 * Collapse scored variants into product groups.
 *
 * @param filtered when true, a colour is kept only if some in-store size of it
 *   passes `worthShowing`; a product with no such colour drops out entirely.
 *   Passed false to build the never-empty fallback.
 */
const buildFitGroups = (items: FitItem[], filtered: boolean): ProductGroup[] => {
  const byProduct = new Map<string, FitItem[]>();
  for (const item of items) {
    const key = productKeyOf(item);
    const bucket = byProduct.get(key);
    if (bucket) bucket.push(item);
    else byProduct.set(key, [item]);
  }

  const groups: ProductGroup[] = [];
  byProduct.forEach((rows, key) => {
    const recommended = rows.find((r) => r.recommended_size)?.recommended_size ?? null;

    const byColor = new Map<string, FitItem[]>();
    for (const r of rows) {
      const color = (r.color || '').trim();
      const bucket = byColor.get(color);
      if (bucket) bucket.push(r);
      else byColor.set(color, [r]);
    }

    const options: ColorOption[] = [];
    let best = -1;
    byColor.forEach((colorRows) => {
      // 建議尺寸 first, then best score — so a colour the store HAS in the
      // recommended size never advertises one of its other sizes.
      const ranked = [...colorRows].sort((a, b) => {
        const aOn = recommended != null && sameSize(a.size_in_store, recommended) ? 0 : 1;
        const bOn = recommended != null && sameSize(b.size_in_store, recommended) ? 0 : 1;
        return aOn - bOn || b.score - a.score;
      });
      const chosen = filtered ? ranked.find(worthShowing) : ranked[0];
      if (!chosen) return;
      if (chosen.score > best) best = chosen.score;
      options.push({
        row: chosen,
        color: chosen.color,
        size_in_store: chosen.size_in_store,
        fit_label: chosen.fit_label,
        waist_estimated: chosen.waist_estimated,
        score: chosen.score,
        caption: fitOptionCaption(chosen),
      });
    });
    if (options.length === 0) return;

    // Recommended-size colours first, best score next — the same top-down read
    // the backend gives the flat list.
    options.sort((a, b) => {
      const aOn = recommended != null && sameSize(a.size_in_store, recommended) ? 0 : 1;
      const bOn = recommended != null && sameSize(b.size_in_store, recommended) ? 0 : 1;
      return aOn - bOn || (b.score ?? 0) - (a.score ?? 0);
    });

    const head = options[0].row;
    groups.push({
      key,
      title: head.product_title,
      price: head.price,
      image_url: head.image_url ?? null,
      recommended_size: recommended,
      options,
      best_score: best,
    });
  });

  groups.sort((a, b) => b.best_score - a.best_score);
  return groups;
};

// Same collapse for the 尚未建檔尺寸 list — it fragments per variant exactly
// the same way. Deliberately NO fit filtering: "we have not measured this" and
// "this does not fit" call for opposite reactions from a staff member.
const buildUnmeasuredGroups = (items: UnmeasuredItem[]): ProductGroup[] => {
  const byProduct = new Map<string, UnmeasuredItem[]>();
  for (const item of items) {
    const key = productKeyOf(item);
    const bucket = byProduct.get(key);
    if (bucket) bucket.push(item);
    else byProduct.set(key, [item]);
  }
  const groups: ProductGroup[] = [];
  byProduct.forEach((rows, key) => {
    const seen = new Set<string>();
    const options: ColorOption[] = [];
    for (const r of rows) {
      const dedupe = `${(r.color || '').trim()}::${String(r.size_in_store ?? '').trim()}`;
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      options.push({
        row: r,
        color: r.color,
        size_in_store: r.size_in_store,
        fit_label: null,
        waist_estimated: false,
        score: null,
        caption: `店內 ${orDash(r.size_in_store)} · ${unmeasuredReasonLabel(r.reason)}`,
      });
    }
    if (options.length === 0) return;
    groups.push({
      key,
      title: rows[0].product_title,
      price: rows[0].price,
      image_url: rows[0].image_url ?? null,
      recommended_size: null,
      options,
      best_score: 0,
    });
  });
  return groups;
};

// How many products the fit filter removed, plus the never-empty guarantee.
// A staffer looking at an empty list concludes the store has nothing for this
// customer — worse than showing an imperfect garment with an honest caption.
const FALLBACK_GROUP_COUNT = 3;

interface FitListView {
  groups: ProductGroup[];
  hiddenProducts: number;
  fallback: boolean;
}

const buildFitListView = (items: FitItem[] | null): FitListView => {
  if (items === null || items.length === 0) {
    return { groups: [], hiddenProducts: 0, fallback: false };
  }
  const all = buildFitGroups(items, false);
  const kept = buildFitGroups(items, true);
  if (kept.length > 0) {
    return { groups: kept, hiddenProducts: Math.max(0, all.length - kept.length), fallback: false };
  }
  return { groups: all.slice(0, FALLBACK_GROUP_COUNT), hiddenProducts: 0, fallback: true };
};

// Idempotency key for POST /api/noke/sales. crypto.randomUUID with a fallback
// for older Android webviews that lack it.
const newClientRef = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `noke-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// ---- 掛單 (parked tickets) -------------------------------------------------
// Customer A has a ticket going, then heads back into the fitting room; B wants
// to pay now. A's ticket is parked whole — lines, discount, 結帳會員 and its
// client_ref — and 叫回 restores it exactly. The client_ref travels with it so a
// ticket whose submit timed out before it was parked still dedupes on resubmit.
// Persisted per device: a reload or a dead tablet battery must not make staff
// re-scan a cart while the customer stands there. Nothing is reserved on the
// server — the garments are physically in the customer's hands.
const PARKED_STORAGE_KEY = 'nokePosParkedTickets';

interface ParkedTicket {
  id: string;
  note: string;
  parked_at: number;
  lines: TicketLine[];
  discount: string;
  ticket_phone: string;
  ticket_customer: Customer | null;
  ticket_customer_looked: boolean;
  client_ref: string | null;
  // Display only — the ticket is re-quoted by the server when it is resumed.
  total: number;
}

// Storage can be blocked (private mode, full quota); the tray then simply
// lives for the page's lifetime instead of breaking the till.
const loadParkedTickets = (): ParkedTicket[] => {
  try {
    const raw = localStorage.getItem(PARKED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((t) => Array.isArray(t?.lines) && t.lines.length > 0) : [];
  } catch {
    return [];
  }
};

const saveParkedTickets = (tickets: ParkedTicket[]): void => {
  try {
    if (tickets.length === 0) localStorage.removeItem(PARKED_STORAGE_KEY);
    else localStorage.setItem(PARKED_STORAGE_KEY, JSON.stringify(tickets));
  } catch {
    /* storage unavailable — tray stays in memory only */
  }
};

// Who this parked ticket belongs to, in the words staff would use: their own
// note first (「黑外套先生」), else the 會員, else the phone.
const parkedTicketLabel = (t: ParkedTicket, index: number): string =>
  t.note || t.ticket_customer?.name || t.ticket_phone.trim() || `掛單 ${index + 1}`;

const parkedTicketSummary = (t: ParkedTicket): string => {
  const pieces = t.lines.reduce((s, l) => s + l.qty, 0);
  const first = t.lines[0].product_title
    .replace(/^【[^】]*】\s*/, '')
    .replace(/^(🇰🇷)?\s*(韓製|韓國設計)\s*/, '');
  return pieces > 1 ? `${first} 等 ${pieces} 件` : first;
};

const parkedTimeLabel = (ms: number): string => {
  const d = new Date(ms);
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return d.toDateString() === new Date().toDateString() ? hhmm : `${d.getMonth() + 1}/${d.getDate()} ${hhmm}`;
};

// Short feedback beep via WebAudio + haptic buzz on a successful scan.
const feedbackBeep = (): void => {
  try {
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
      osc.onended = () => ctx.close();
    }
  } catch {
    /* audio not available — non-fatal */
  }
  if (navigator.vibrate) {
    try {
      navigator.vibrate(80);
    } catch {
      /* vibrate not available — non-fatal */
    }
  }
};

// The two moments this page serves. Each owns its own state and its own reset;
// nothing in one may clear the other's.
type PosTab = 'fitting' | 'checkout' | 'manual';

const NokePosPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();

  // Default 收銀: ringing up is the far more frequent action, it is the only
  // moment with money and a mall-POS number on the line, and it is what a scan
  // landing on a freshly opened page should do. 試衣 is entered on purpose.
  const [tab, setTab] = useState<PosTab>('checkout');
  // The camera scanner captures its decode callback once at start(), so the
  // active tab is read through a ref — otherwise a camera started on 收銀 would
  // keep adding to the ticket after the staffer switched to 試衣.
  const tabRef = useRef<PosTab>('checkout');
  tabRef.current = tab;
  // 試穿計數 panel — scans on the 試衣 tab are handed to it (see doLookup).
  const tryonRef = useRef<TryonPanelHandle>(null);
  // 量身找尺寸: the old 顧客卡 / 合身清單 tools, folded away under the scan
  // counter. Kept for staff who want them; not the tab's job any more.
  const [sizingOpen, setSizingOpen] = useState(false);
  // 手冊 pulls a ~120KB index and page images; don't spend that on staff who
  // never open it, and don't unmount it once they have (keeps their place).
  const [mountedManual, setMountedManual] = useState(false);
  useEffect(() => {
    if (tab === 'manual') setMountedManual(true);
  }, [tab]);

  const [lines, setLines] = useState<TicketLine[]>([]);
  const [discount, setDiscount] = useState('');
  // 現金找零. Ticket-scoped and display-only: cleared with the ticket, never
  // sent to the server. Collapsed by default so the sticky bar stays short on a
  // phone; the 找零 amount stays on the button when it is folded away.
  const [cashOpen, setCashOpen] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [looking, setLooking] = useState(false);

  // Scanner
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanRef = useRef<{ code: string; time: number }>({ code: '', time: 0 });

  // 會員 being SERVED (顧客卡 / 試衣 tab). Belongs to the customer lifecycle:
  // survives a completed sale, cleared only by 換顧客.
  const [customerPhone, setCustomerPhone] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerLooked, setCustomerLooked] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);

  // 會員 being CHARGED (收銀 tab). Belongs to the ticket lifecycle: this is the
  // phone that goes on the sale, and it is cleared with the ticket. Separate
  // from the field above on purpose — the person at the fitting room and the
  // person at the till are often not the same person.
  const [ticketPhone, setTicketPhone] = useState('');
  const [ticketCustomer, setTicketCustomer] = useState<Customer | null>(null);
  const [ticketCustomerLooked, setTicketCustomerLooked] = useState(false);
  const [ticketCustomerLoading, setTicketCustomerLoading] = useState(false);

  // ---- 顧客卡 ------------------------------------------------------------
  // ONE customer context for the whole visit. 建議尺寸, 店內合身清單 and
  // 商品答案卡 all read this — there is deliberately no second body-entry form
  // anywhere on the page, because a staff member re-keying 身高體重 mid-sale is a
  // staff member who stops logging.
  const [cardOpen, setCardOpen] = useState(true);
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [build, setBuild] = useState<BuildCode>('standard');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionBody, setSessionBody] = useState<BodyMeasure | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [sessionSaving, setSessionSaving] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // ---- 店內合身清單 -------------------------------------------------------
  const [fitItems, setFitItems] = useState<FitItem[] | null>(null);
  const [fitBody, setFitBody] = useState<BodyMeasure | null>(null);
  const [fitSkipped, setFitSkipped] = useState(0);
  const [fitUnmeasured, setFitUnmeasured] = useState<UnmeasuredItem[]>([]);
  const [fitNotGarment, setFitNotGarment] = useState(0);
  const [unmeasuredOpen, setUnmeasuredOpen] = useState(false);
  const [fitLoading, setFitLoading] = useState(false);
  const [fitError, setFitError] = useState<string | null>(null);

  // ---- 商品答案卡 ---------------------------------------------------------
  const [sheetVariantId, setSheetVariantId] = useState<number | null>(null);
  const [sheetFallback, setSheetFallback] = useState<SheetFallback | null>(null);
  // Latest openProductCard, reachable from the scanner callbacks. The camera
  // scanner freezes its decode closure at start() and doLookup is memoised, so
  // calling the function directly would pin an old `sessionId` and the sheet
  // would stop showing 建議尺寸 after the 顧客卡 was created.
  const openProductCardRef = useRef<
    ((variantId: number, fallback: SheetFallback, addable?: LookupItem | null) => void) | null
  >(null);
  const [sheetCard, setSheetCard] = useState<ProductCard | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  // Everything needed to drop this garment onto the ticket. Set whenever the
  // sheet was opened from something NOT already on the ticket (a 試衣-tab scan,
  // a 合身清單 row), so the answer card is never a dead end: the customer says
  // "I'll take it" and 加入結帳 is right there. Null for a sheet opened from a
  // ticket line, which is on the ticket already.
  const [sheetAddable, setSheetAddable] = useState<LookupItem | null>(null);

  // ---- 複製商品連結 -------------------------------------------------------
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyOk, setCopyOk] = useState(false);

  // 換顧客 confirmation. The ONLY path that wipes body/session/合身清單, so it
  // asks first — but it sits in the 顧客卡 header, not behind a menu.
  const [switchCustomerOpen, setSwitchCustomerOpen] = useState(false);

  // Salesperson (persisted). Picked from the 門市 roster, never typed: free text
  // produced 'eric', 'Eric' and 'ericNK8384455' for the same person — the last
  // of those is a scanner burst that landed in the focused field instead of on
  // the ticket (useBarcodeScanner never steals keys from a text input). A
  // picker fixes the attribution AND stops the till swallowing scans.
  const [salesperson, setSalesperson] = useState('');
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  // Roster unreachable → fall back to the old free-text field. Never block the
  // till on a staff-list request.
  const [staffFailed, setStaffFailed] = useState(false);
  // Set the moment a name is tapped, so a slow roster response can never
  // overwrite a choice the staffer already made while it was in flight.
  const salespersonTouchedRef = useRef(false);

  // Submission / result
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState<CompletedSale | null>(null);
  // Cleared together with `completed` (see resetTicket) so a finished sale's
  // 補貨 note can never linger onto the next customer's ticket.
  const [restock, setRestock] = useState<RestockHint[]>([]);
  // Which hints the staffer has already actioned, and which one is in flight.
  // Kept alongside `restock` (not folded into it) so the line stays on screen
  // after the tap — a row that vanishes gives no confirmation it was the one
  // you meant, and the next customer's ticket clears the whole panel anyway.
  const [restocked, setRestocked] = useState<Set<number>>(new Set());
  const [restockBusy, setRestockBusy] = useState<number | null>(null);

  // 活動折扣. The ticket is priced by the SERVER (POST /api/noke/quote) rather
  // than in this component: staff key 應收總額 and 品牌分帳 into the mall POS from
  // this screen, so a client-side copy of the campaign rules that drifted from
  // the backend would send them to key a wrong number and nobody would notice
  // until 月結. One engine, one answer.
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [activeCampaigns, setActiveCampaigns] = useState<ActiveCampaign[]>([]);

  // Idempotency: one client_ref per TICKET (not per attempt). Generated when the
  // ticket gets its first item, reused on every retry of that ticket so the
  // backend can dedupe a resubmit after a network timeout, and cleared when the
  // ticket empties or the sale succeeds.
  const clientRefRef = useRef<string | null>(null);

  // 掛單 tray — see ParkedTicket.
  const [parked, setParked] = useState<ParkedTicket[]>(loadParkedTickets);
  const [parkOpen, setParkOpen] = useState(false);
  const [parkNote, setParkNote] = useState('');
  // 刪除 asks first: it throws away a cart a customer may still come back for.
  const [discardParked, setDiscardParked] = useState<ParkedTicket | null>(null);
  useEffect(() => {
    saveParkedTickets(parked);
  }, [parked]);

  // Who is ringing this up. Default to the logged-in account when it IS one of
  // the 門市人員 — but the counter tablet is often the shared 'NOKE 門市' login,
  // which is not a person, so on that account nothing is preselected and a name
  // must be tapped. Falls back to the last name used on this tablet (matched
  // case-insensitively against the roster, so a stored 'eric' resolves to the
  // roster's 'Eric' rather than minting a second spelling).
  useEffect(() => {
    const stored = localStorage.getItem(SALESPERSON_STORAGE_KEY) || '';
    setSalesperson(stored);
    let cancelled = false;
    (async () => {
      try {
        const list = await timeClockService.getCounterStaff();
        if (cancelled) return;
        setStaffOptions(list);
        setStaffFailed(false);
        const self = list.find((s) => s.id === currentUser?.id);
        if (salespersonTouchedRef.current) return;
        const match =
          self ||
          list.find((s) => s.name.toLowerCase() === stored.trim().toLowerCase());
        setSalesperson(match ? match.name : '');
      } catch (err) {
        console.error('Failed to fetch counter staff list:', err);
        if (!cancelled) setStaffFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (lines.length > 0 && !clientRefRef.current) {
      clientRefRef.current = newClientRef();
    } else if (lines.length === 0) {
      clientRefRef.current = null;
    }
  }, [lines]);

  // Which activities are running today — shown as a banner so staff can answer
  // 「為什麼是 950？」 without calling anyone.
  useEffect(() => {
    api
      .get('/api/noke/campaigns/active')
      .then((res) => setActiveCampaigns(res.data.campaigns || []))
      .catch(() => setActiveCampaigns([]));
  }, []);

  const quoteSeq = useRef(0);

  const gross = lines.reduce((s, l) => s + (l.is_gift ? 0 : l.unit_price * l.qty), 0);
  // Clamp the manual discount to [0, gross] — this clamped value is BOTH what is
  // displayed and what is sent; never send a raw out-of-range input. (When
  // 活動折扣 apply, the server clamps further against the post-campaign subtotal.)
  const discountNum = Math.min(Math.max(0, parseFloat(discount) || 0), gross);

  // Local estimate, used ONLY as the pre-quote placeholder. Everything staff
  // acts on comes from `quote` — see the quote effect below.
  const localVendorTotals = allocateVendorTotals(
    lines.filter((l) => !l.is_gift),
    discountNum
  );

  const netTotal = quote ? quote.net_total : Math.max(0, gross - discountNum);
  // Change is computed against the SAME netTotal on the screen, so a re-quote
  // (a scanned line, a changed discount) moves the 找零 with it rather than
  // leaving a stale number the staffer would pay out.
  const cashNum =
    cashReceived.trim() === '' ? null : Math.max(0, Math.round(parseFloat(cashReceived) || 0));
  const changeDue = cashNum === null ? null : cashNum - Math.round(netTotal);
  const vendorTotals = quote ? quote.vendor_totals : localVendorTotals;
  // Per-line campaign discounts, indexed the same way the ticket is keyed.
  const quoteByLine = new Map<string, QuoteLine>();
  if (quote) {
    quote.lines.forEach((ql) => quoteByLine.set(lineKey(ql.variant_id, !!ql.is_gift), ql));
  }

  // Re-price on every ticket change (debounced). `quoteSeq` guards against an
  // out-of-order response overwriting a newer one — with fast scanning the
  // requests genuinely can land out of order.
  useEffect(() => {
    if (lines.length === 0) {
      setQuote(null);
      setQuoteError(null);
      setQuoting(false);
      return;
    }
    const seq = ++quoteSeq.current;
    setQuoting(true);
    const t = setTimeout(() => {
      api
        .post('/api/noke/quote', {
          items: lines.map((l) => ({
            variant_id: l.variant_id,
            qty: l.qty,
            unit_price: l.is_gift ? undefined : l.unit_price,
            is_gift: l.is_gift || undefined,
          })),
          discount: discountNum || undefined,
        })
        .then((res) => {
          if (seq !== quoteSeq.current) return;
          setQuote(res.data);
          setQuoteError(null);
        })
        .catch((err) => {
          if (seq !== quoteSeq.current) return;
          setQuote(null);
          setQuoteError(apiMessage(err, '無法計算金額，請確認網路'));
        })
        .finally(() => {
          if (seq === quoteSeq.current) setQuoting(false);
        });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, discountNum]);

  // Add a scanned/looked-up item; increment qty when the variant is already on
  // the ticket as a PAID line (a 贈品 line of the same variant stays separate).
  const addItem = useCallback((item: LookupItem) => {
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.variant_id === item.variant_id && !l.is_gift);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { ...item, qty: 1, unit_price: item.price, is_gift: false }];
    });
  }, []);

  const doLookup = useCallback(
    async (rawCode: string, fromScanner: boolean) => {
      const code = rawCode.trim();
      if (!code) return;

      // Debounce repeated camera reads of the same tag within ~1.5s.
      if (fromScanner) {
        const now = Date.now();
        const same = code === lastScanRef.current.code && now - lastScanRef.current.time < 1500;
        // 試衣: slide the window on every read, so a tag held in front of the
        // camera (10 fps) never becomes a second, phantom 試穿. 收銀 keeps the
        // fixed window — its qty bump is visible on the ticket.
        if (same && tabRef.current === 'fitting') lastScanRef.current = { code, time: now };
        if (same) return;
        lastScanRef.current = { code, time: now };
      }

      // 試衣 tab: a scan IS the 試穿 record — one hang tag, one piece into the
      // fitting room. POST /tryons resolves the code itself (one round trip on
      // the mall wifi), so skip the lookup. The 商品答案卡 is still one tap away
      // on the confirmation. Read through tabRef: the camera callback is frozen.
      if (tabRef.current === 'fitting') {
        tryonRef.current?.record(code);
        return;
      }

      setLooking(true);
      try {
        const res = await api.get(`/api/noke/lookup/${encodeURIComponent(code)}`);
        if (res.data.found && res.data.item) {
          const item: LookupItem = res.data.item;
          feedbackBeep();
          addItem(item);
          if (item.qty_on_hand <= 0) {
            enqueueSnackbar(`${item.sku} NOKE 在櫃庫存不足，仍已加入；請確認商品已到櫃`, { variant: 'warning' });
          }
        } else {
          enqueueSnackbar(`找不到條碼 ${code}`, { variant: 'error' });
        }
      } catch (err: any) {
        enqueueSnackbar(err.response?.data?.message || '查詢失敗', { variant: 'error' });
      } finally {
        setLooking(false);
      }
    },
    [addItem, enqueueSnackbar]
  );

  // ---- Hardware scanner (HID keyboard wedge) ------------------------------
  // Always active on this page, no input focus needed. Bursts go through the
  // SAME path as camera scans (fromScanner=true → same beep/vibrate, same 1.5s
  // duplicate debounce, same low-stock warning). When a text field is focused,
  // the hook stays hands-off — a scan into the manual barcode field submits via
  // that field's own Enter handler.
  useBarcodeScanner({ onScan: (code) => doLookup(code, true) });

  // ---- Camera scanner lifecycle (opt-in) -----------------------------------

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;
    try {
      const html5 = new Html5Qrcode(SCANNER_ELEMENT_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128],
        verbose: false,
      });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 120 } },
        (decodedText: string) => {
          doLookup(decodedText, true);
        },
        () => {
          /* per-frame decode failure — ignore (fires constantly) */
        }
      );
      setScanning(true);
      localStorage.setItem(CAMERA_PREF_STORAGE_KEY, '1');
    } catch (err: any) {
      scannerRef.current = null;
      setScanning(false);
      localStorage.setItem(CAMERA_PREF_STORAGE_KEY, '0');
      enqueueSnackbar('無法開啟相機，請改用掃描槍或手動輸入條碼', { variant: 'error' });
    }
  }, [doLookup, enqueueSnackbar]);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      await scanner.stop();
      await scanner.clear();
    } catch {
      /* already stopped — non-fatal */
    }
    scannerRef.current = null;
    setScanning(false);
    localStorage.setItem(CAMERA_PREF_STORAGE_KEY, '0');
  }, []);

  // Camera is opt-in: auto-start only if it was left ON last session.
  useEffect(() => {
    if (localStorage.getItem(CAMERA_PREF_STORAGE_KEY) === '1') {
      startScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop the camera on unmount to release the device (does NOT touch the saved
  // preference — leaving the page is not the same as turning the camera off).
  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;
      if (scanner) {
        scanner.stop().catch(() => undefined);
        scannerRef.current = null;
      }
    };
  }, []);

  // ---- Ticket editing -----------------------------------------------------

  const changeQty = (key: string, delta: number) => {
    setLines((prev) =>
      prev
        .map((l) => (lineKey(l.variant_id, l.is_gift) === key ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0)
    );
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => lineKey(l.variant_id, l.is_gift) !== key));
  };

  // Mark a line as the 滿額贈 giveaway: price drops to 0 but the piece still
  // leaves 庫存, so the counter's stock count stays honest.
  const toggleGift = (key: string) => {
    setLines((prev) => {
      const target = prev.find((l) => lineKey(l.variant_id, l.is_gift) === key);
      if (!target) return prev;
      const nextGift = !target.is_gift;
      // Merge into an existing line of the same variant on the other side.
      const twin = prev.find((l) => l.variant_id === target.variant_id && l.is_gift === nextGift);
      if (twin) {
        return prev
          .map((l) =>
            l === twin ? { ...l, qty: l.qty + target.qty } : l
          )
          .filter((l) => l !== target);
      }
      return prev.map((l) => (l === target ? { ...l, is_gift: nextGift } : l));
    });
  };

  // ---- Member lookup ------------------------------------------------------

  // One request shape, two independent 會員 slots (顧客卡 vs 收銀).
  const fetchCustomer = async (phone: string): Promise<Customer | null> => {
    const res = await api.get(`/api/noke/customer-by-phone/${encodeURIComponent(phone)}`);
    return res.data.found && res.data.customer ? (res.data.customer as Customer) : null;
  };

  const lookupCustomer = async () => {
    const phone = customerPhone.trim();
    if (!phone) return;
    setCustomerLoading(true);
    setCustomerLooked(false);
    try {
      setCustomer(await fetchCustomer(phone));
      setCustomerLooked(true);
    } catch (err: any) {
      enqueueSnackbar(apiMessage(err, '會員查詢失敗'), { variant: 'error' });
    } finally {
      setCustomerLoading(false);
    }
  };

  // 收銀 tab's own 會員 lookup — this is the phone that lands on the sale.
  const lookupTicketCustomer = async () => {
    const phone = ticketPhone.trim();
    if (!phone) return;
    setTicketCustomerLoading(true);
    setTicketCustomerLooked(false);
    try {
      setTicketCustomer(await fetchCustomer(phone));
      setTicketCustomerLooked(true);
    } catch (err: any) {
      enqueueSnackbar(apiMessage(err, '會員查詢失敗'), { variant: 'error' });
    } finally {
      setTicketCustomerLoading(false);
    }
  };

  // 帶入試衣顧客: the person being fitted IS the person paying. One tap rather
  // than re-keying a phone number, and never automatic — the whole reason the
  // two slots exist is that they are frequently different people.
  const useFittingCustomerOnTicket = () => {
    const phone = customerPhone.trim();
    if (!phone) return;
    setTicketPhone(phone);
    setTicketCustomer(customer);
    setTicketCustomerLooked(customerLooked);
  };

  // ---- 顧客卡 -------------------------------------------------------------

  const heightNum = parseFloat(heightCm);
  const weightNum = parseFloat(weightKg);
  const bodyReady = heightNum > 0 && weightNum > 0;

  // Body payload used when there is no session — /fit-scan accepts the raw
  // measurements too, so a failed POST /session (mall wifi, backend hiccup)
  // downgrades the feature instead of killing it.
  const rawBody = () => ({ height_cm: heightNum, weight_kg: weightNum, build });

  const saveSession = async () => {
    if (!bodyReady) {
      enqueueSnackbar('請先輸入身高與體重', { variant: 'warning' });
      return;
    }
    setSessionSaving(true);
    setSessionError(null);
    try {
      const res = await api.post('/api/noke/session', {
        phone: customerPhone.trim() || undefined,
        ...rawBody(),
      });
      const data: SessionResponse = res.data;
      setSessionId(data.session_id || null);
      setSessionBody(data.body || null);
      setProfileSaved(!!data.profile_saved);
      // The session lookup knows the member too — keep the 會員 block in sync
      // rather than making staff press 查詢 a second time.
      if (data.customer) {
        setCustomer((prev) => ({
          id: data.customer!.id,
          name: data.customer!.name,
          phone: prev?.phone || customerPhone.trim(),
          is_vip: data.customer!.is_vip,
          credit_balance: prev?.credit_balance ?? 0,
        }));
        setCustomerLooked(true);
      }
      enqueueSnackbar('顧客卡已建立', { variant: 'success' });
    } catch (err: any) {
      setSessionId(null);
      setSessionBody(null);
      setProfileSaved(false);
      setSessionError(
        apiMessage(err, '無法建立顧客卡（可能是網路問題），仍可直接按「找適合的」試算尺寸')
      );
    } finally {
      setSessionSaving(false);
    }
  };

  // Any edit to the body inputs invalidates the session — silently keeping an
  // old session_id would pair 建議尺寸 with the wrong measurements.
  const invalidateSession = () => {
    setSessionId(null);
    setSessionBody(null);
    setProfileSaved(false);
    // The old failure no longer describes the body now on screen.
    setSessionError(null);
    setFitItems(null);
    setFitBody(null);
    setFitSkipped(0);
    setFitUnmeasured([]);
    setFitNotGarment(0);
    setUnmeasuredOpen(false);
  };

  // ---- 建議尺寸 + 店內合身清單 --------------------------------------------

  const runFitScan = async () => {
    if (!sessionId && !bodyReady) {
      setCardOpen(true);
      enqueueSnackbar('請先填寫顧客卡的身高與體重', { variant: 'warning' });
      return;
    }
    setFitLoading(true);
    setFitError(null);
    try {
      const res = await api.post('/api/noke/fit-scan', sessionId ? { session_id: sessionId } : rawBody());
      const data: FitScanResponse = res.data;
      setFitItems(Array.isArray(data.items) ? data.items : []);
      setFitBody(data.body || null);
      setFitSkipped(data.skipped_no_measurements || 0);
      // Non-garments never reach the UI; everything else on the rail does.
      setFitUnmeasured(Array.isArray(data.unmeasured) ? data.unmeasured.filter(isGarment) : []);
      setFitNotGarment(data.skipped_not_a_garment || 0);
      setUnmeasuredOpen(false);
    } catch (err: any) {
      setFitItems(null);
      setFitBody(null);
      setFitSkipped(0);
      setFitUnmeasured([]);
      setFitNotGarment(0);
      setFitError(apiMessage(err, '無法取得合身清單，請確認網路後再試'));
    } finally {
      setFitLoading(false);
    }
  };

  // A 合身清單 row tapped onto the ticket — scored or unmeasured, same path.
  // /fit-scan now carries the real vendor, so the pre-quote Metanoia/MdoC split
  // on the sticky bar is right from the first tap (the server quote still has
  // the final word, and 完成銷售 stays disabled until it lands). qty_on_hand is
  // 1 because the item is physically on the rail in front of the staff member.
  const scanRowToLookupItem = (item: ScanRow): LookupItem => ({
    variant_id: item.variant_id,
    sku: item.sku,
    product_title: item.product_title,
    vendor: item.vendor || '',
    color: item.color,
    size: item.size_in_store,
    price: item.price,
    qty_on_hand: 1,
    handle: item.handle ?? null,
    image_url: item.image_url ?? null,
  });

  const addScanItem = (item: ScanRow) => {
    addItem(scanRowToLookupItem(item));
    enqueueSnackbar(`已加入結帳：${item.product_title}`, { variant: 'success' });
  };

  // ---- 商品答案卡 ---------------------------------------------------------

  const openProductCard = async (
    variantId: number,
    fallback: SheetFallback,
    addable?: LookupItem | null
  ) => {
    setSheetVariantId(variantId);
    setSheetFallback(fallback);
    setSheetAddable(addable ?? null);
    setSheetCard(null);
    setSheetError(null);
    setSheetLoading(true);
    try {
      const qs = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
      const res = await api.get(`/api/noke/product-card/${variantId}${qs}`);
      setSheetCard(res.data || null);
    } catch (err: any) {
      setSheetCard(null);
      setSheetError(apiMessage(err, '目前讀不到商品資料，請確認網路後再試一次'));
    } finally {
      setSheetLoading(false);
    }
  };
  // Republished every render so the scanner callbacks always call the version
  // that knows the current sessionId.
  openProductCardRef.current = openProductCard;

  const closeProductCard = () => {
    setSheetVariantId(null);
    setSheetFallback(null);
    setSheetAddable(null);
    setSheetCard(null);
    setSheetError(null);
  };

  // 加入結帳 from inside the answer card. Stays on the current tab — in service
  // mode the staffer is still with the customer; the 收銀 tab badge shows the
  // item landed.
  const addSheetItemToTicket = () => {
    if (!sheetAddable) return;
    addItem(sheetAddable);
    enqueueSnackbar(`已加入結帳：${sheetAddable.product_title}`, { variant: 'success' });
    closeProductCard();
  };

  // ---- 試穿 (from the 合身清單) -------------------------------------------
  // The same record as a 試衣-tab scan: the variant's own NK code goes through
  // TryonPanel, so there is ONE 試穿 signal. (The old 買了/沒買 outcome sheet is
  // gone — it logged nothing in a month, and sales already say what was bought.)
  const recordTryon = (variantId: number) => {
    tryonRef.current?.record(`NK${variantId}`);
  };

  // ---- 複製商品連結 -------------------------------------------------------

  // One block, ready to paste into the official LINE account.
  const linkableLines = lines.filter((l) => !!l.handle);
  const missingLinkCount = lines.length - linkableLines.length;
  const linkText = linkableLines
    .map((l) => `${l.product_title} ${l.color}${l.size ? ` ${l.size}` : ''}\n${pdpUrl(l.handle as string)}`)
    .join('\n\n');

  const copyLinks = async () => {
    if (!linkText) return;
    setCopyOpen(true);
    setCopyOk(false);
    try {
      // navigator.clipboard is undefined on insecure origins and can reject
      // outright on mall wifi / restricted browsers — the visible text block
      // below is the real fallback, so a failure here is not an error state.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(linkText);
        setCopyOk(true);
        enqueueSnackbar('已複製，可直接貼到 LINE', { variant: 'success' });
      }
    } catch {
      setCopyOk(false);
    }
  };

  // ---- Complete sale ------------------------------------------------------

  const handleComplete = async () => {
    if (lines.length === 0) {
      enqueueSnackbar('尚未加入任何商品', { variant: 'warning' });
      return;
    }
    if (salesperson.trim()) {
      localStorage.setItem(SALESPERSON_STORAGE_KEY, salesperson.trim());
    }
    setSubmitting(true);
    try {
      const payload = {
        items: lines.map((l) => ({
          variant_id: l.variant_id,
          qty: l.qty,
          // A 贈品 is priced by the server (0); sending a unit_price would be
          // rejected as a non-positive override.
          unit_price: l.is_gift ? undefined : l.unit_price,
          is_gift: l.is_gift || undefined,
        })),
        // discountNum is already clamped to [0, gross] above; the server applies
        // 活動折扣 itself and clamps this against what remains.
        discount: discountNum || undefined,
        // The 收銀 phone — the person PAYING. Never the 顧客卡 phone, which may
        // belong to someone still in the fitting room.
        customer_phone: ticketPhone.trim() || undefined,
        salesperson: salesperson.trim() || undefined,
        // Same client_ref on every retry of this ticket — backend dedupes.
        client_ref: clientRefRef.current || undefined,
      };
      const res = await api.post('/api/noke/sales', payload);
      // duplicate: true means a retry hit a sale the backend already recorded
      // for this client_ref — that IS success, never resubmit or error out.
      if (res.data.duplicate) {
        enqueueSnackbar('此筆銷售先前已成功送出，未重複入帳', { variant: 'info' });
      }
      const sale: CompletedSale | undefined = res.data.sale;
      setRestock((res.data.restock as RestockHint[]) || []);
      setRestocked(new Set());
      const warnings: string[] = res.data.warnings || [];
      warnings.forEach((w) => enqueueSnackbar(w, { variant: 'warning' }));
      clientRefRef.current = null;
      if (sale) {
        setCompleted(sale);
      } else {
        // Defensive: duplicate ack without the sale body — still a success.
        enqueueSnackbar('銷售完成', { variant: 'success' });
        resetTicket();
      }
    } catch (err: any) {
      // Network failure (no response): keep the ticket AND its client_ref intact
      // so staff can retry without re-scanning or risking a double submit.
      if (!err.response) {
        enqueueSnackbar('連線失敗，請確認網路後重試 (訂單已保留)', { variant: 'error' });
      } else {
        // Includes the backend's 400 for an out-of-range discount.
        enqueueSnackbar(apiMessage(err, '結帳失敗'), { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---- The two resets ------------------------------------------------------
  // Read these together. Nothing in resetTicket may touch customer state and
  // nothing in resetCustomer may touch the ticket — that coupling is the bug
  // this page was rebuilt to remove.

  // 繼續下一筆 / a completed sale. TICKET ONLY. A sale ending is not a customer
  // leaving: the staffer may still be mid-fitting with someone else, whose
  // 身高體重, session and 合身清單 must all still be on screen afterwards.
  // 補好了: put the variant back on the shelf flag the sale cleared. Reuses
  // POST /inventory/set-slot, which applies on_display independently — the
  // 櫃位 the staffer just fetched from is left exactly as it was.
  const markRestocked = async (variantId: number) => {
    setRestockBusy(variantId);
    try {
      await api.post('/api/noke/inventory/set-slot', {
        variant_id: variantId,
        on_display: true,
      });
      setRestocked((prev) => new Set(prev).add(variantId));
    } catch (err: any) {
      // Never block the till on this — the sale is already committed and the
      // staffer can fix the flag from 庫存 later.
      enqueueSnackbar(err.response?.data?.message || '標記補貨失敗', { variant: 'error' });
    } finally {
      setRestockBusy(null);
    }
  };

  const resetTicket = () => {
    setCompleted(null);
    setRestock([]);
    clientRefRef.current = null;
    setLines([]);
    setDiscount('');
    setManualCode('');
    setCashReceived('');
    setCashOpen(false);
    // The 會員 on the TICKET only. 顧客卡's phone/member is untouched.
    setTicketPhone('');
    setTicketCustomer(null);
    setTicketCustomerLooked(false);
    setCopyOpen(false);
    setCopyOk(false);
  };

  // ---- 掛單 / 叫回 -----------------------------------------------------------
  // Ticket state only, same boundary as resetTicket: parking a cart never
  // touches the 顧客卡 of whoever is in the fitting room.

  const snapshotTicket = (note: string): ParkedTicket => ({
    id: newClientRef(),
    note: note.trim(),
    parked_at: Date.now(),
    lines,
    discount,
    ticket_phone: ticketPhone,
    ticket_customer: ticketCustomer,
    ticket_customer_looked: ticketCustomerLooked,
    client_ref: clientRefRef.current,
    total: netTotal,
  });

  const parkTicket = () => {
    if (lines.length === 0) return;
    const snap = snapshotTicket(parkNote);
    setParked((prev) => [...prev, snap]);
    resetTicket();
    setParkOpen(false);
    setParkNote('');
    enqueueSnackbar(`已掛單：${parkedTicketLabel(snap, parked.length)}`, { variant: 'success' });
  };

  // 叫回. A ticket already in progress is not thrown away — it swaps into the
  // tray, so a staffer who taps the wrong 掛單 loses nothing.
  const resumeTicket = (t: ParkedTicket) => {
    const swapped = lines.length > 0 ? snapshotTicket('') : null;
    setParked((prev) => {
      const rest = prev.filter((p) => p.id !== t.id);
      return swapped ? [...rest, swapped] : rest;
    });
    setCompleted(null);
    setRestock([]);
    // Set BEFORE setLines so the client_ref effect keeps it instead of minting
    // a new one for the restored lines.
    clientRefRef.current = t.client_ref;
    setLines(t.lines);
    setDiscount(t.discount);
    setTicketPhone(t.ticket_phone);
    setTicketCustomer(t.ticket_customer);
    setTicketCustomerLooked(t.ticket_customer_looked);
    setManualCode('');
    setCashReceived('');
    setCashOpen(false);
    setCopyOpen(false);
    setCopyOk(false);
    enqueueSnackbar(
      swapped ? '已叫回；原本的單已改為掛單' : `已叫回：${t.note || t.ticket_customer?.name || t.ticket_phone || '掛單'}`,
      { variant: 'info' }
    );
  };

  const deleteParked = (id: string) => {
    setParked((prev) => prev.filter((p) => p.id !== id));
    setDiscardParked(null);
  };

  // 換顧客 — the ONLY path that clears the customer context, and it clears
  // nothing else. Confirmed by a dialog because it throws away measurements a
  // customer gave out loud; a ticket in progress survives it untouched.
  const resetCustomer = () => {
    setCustomerPhone('');
    setCustomer(null);
    setCustomerLooked(false);
    setHeightCm('');
    setWeightKg('');
    setBuild('standard');
    setSessionId(null);
    setSessionBody(null);
    setProfileSaved(false);
    setSessionError(null);
    setFitItems(null);
    setFitBody(null);
    setFitSkipped(0);
    setFitUnmeasured([]);
    setFitNotGarment(0);
    setUnmeasuredOpen(false);
    setFitError(null);
    setCardOpen(true);
    closeProductCard();
  };

  // True once anything about this customer is on screen — gates 換顧客 so it
  // cannot be tapped when there is nothing to clear.
  const hasCustomerContext =
    !!customerPhone.trim() ||
    !!customer ||
    !!heightCm ||
    !!weightKg ||
    !!sessionId ||
    fitItems !== null;

  // Variant rows → product groups, poor fits removed, never-empty guaranteed.
  // Recomputed only when the scan result changes; the lists are ~90 rows.
  const fitView = useMemo(() => buildFitListView(fitItems), [fitItems]);
  const unmeasuredGroups = useMemo(() => buildUnmeasuredGroups(fitUnmeasured), [fitUnmeasured]);

  // One PRODUCT card for BOTH the scored list and the 尚未建檔尺寸 list. An
  // unmeasured garment is on the same rail and must be just as tappable — it
  // only lacks a 建議尺寸 to print.
  //
  // A product now covers several colours, so the ticket-add moved OFF the card
  // body and ONTO the per-colour chip: the staffer taps 「棕色」, not the
  // product, and a wrong colour can no longer reach the ticket by default.
  // The card body opens the 商品答案卡 instead — informational, reversible,
  // and the same thing the ⓘ button does. 試穿 stays per colour: one tap
  // records that exact variant in the 試穿計數 (TryonPanel).
  const renderProductGroup = (group: ProductGroup, keyPrefix: string) => {
    const head = group.options[0];
    return (
      <Box
        key={`${keyPrefix}-${group.key}`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, borderBottom: '1px solid #eee' }}
      >
        <Box
          onClick={() =>
            openProductCard(
              head.row.variant_id,
              { title: group.title, color: head.color, size: head.size_in_store },
              // Not on the ticket yet — the answer card carries 加入結帳.
              scanRowToLookupItem(head.row)
            )
          }
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
        >
          <PosThumb src={group.image_url} alt={group.title} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {group.title}
            </Typography>
            {/* 建議 is the size chart's ideal; 店內 (on each colour chip below)
                is what is actually on the rail. Two different statements —
                spelled out in words so nobody has to guess which to hand over. */}
            <Typography variant="caption" color="text.secondary" component="div">
              {group.recommended_size ? (
                <>
                  建議尺寸 <strong>{group.recommended_size}</strong> · {formatNT(group.price)}
                </>
              ) : (
                <>尚未算出建議尺寸 · {formatNT(group.price)}</>
              )}
            </Typography>
            {group.options.map((opt) => (
              <Box
                key={opt.row.variant_id}
                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    addScanItem(opt.row);
                  }}
                  label={`${orDash(opt.color)}｜加入結帳`}
                  sx={{ height: 24 }}
                />
                {opt.fit_label && (
                  <Chip
                    size="small"
                    color={fitChipColor(opt.fit_label)}
                    label={opt.fit_label}
                    sx={{ height: 20 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {opt.caption}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 0, px: 1, py: 0, ml: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    recordTryon(opt.row.variant_id);
                  }}
                >
                  試穿
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
        <Tooltip title="商品資訊">
          <IconButton
            size="small"
            onClick={() =>
              openProductCard(
                head.row.variant_id,
                { title: group.title, color: head.color, size: head.size_in_store },
                scanRowToLookupItem(head.row)
              )
            }
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // ---- Success screen -----------------------------------------------------
  // Rendered INSIDE the 收銀 tab, not as a page-level early return. A finished
  // sale must never lock the staffer out of 試衣 — the customer they were
  // serving before customer B walked up to pay is still standing there.
  const completedView = completed ? (
    <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          銷售完成
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 1 }}>
          {completed.sale_ref}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, my: 2 }}>
          {formatNT(completed.net_total)}
        </Typography>
        {/* The customer is still standing there waiting for their change, and
            the ticket that showed it is gone. Recomputed against the COMPLETED
            total, not the ticket's. */}
        {cashNum !== null && cashNum >= completed.net_total && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              收現 {formatNT(cashNum)}
            </Typography>
            <Typography variant="h5" color="success.main" sx={{ fontWeight: 800 }}>
              找零 {formatNT(cashNum - completed.net_total)}
            </Typography>
            {cashNum - completed.net_total > 0 && (
              <Typography variant="caption" color="text.secondary">
                {changeBreakdown(cashNum - completed.net_total)
                  .map((d) => `${d.denom} × ${d.count}`)
                  .join('　')}
              </Typography>
            )}
          </Box>
        )}
        {!!completed.campaign_discount && completed.campaign_discount > 0 && (
          <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
            已套用活動折扣 -{formatNT(completed.campaign_discount)}
            {completed.applied_campaigns && completed.applied_campaigns.length > 0 && (
              <Typography variant="caption" display="block" color="text.secondary">
                {completed.applied_campaigns.map((c) => c.name).join('、')}
              </Typography>
            )}
          </Typography>
        )}
        {/* 補貨 — the counter's most-forgotten task. The piece that just sold was
            usually the one on display, and only the person who put the rest away
            knows there are more. Shown ABOVE 品牌分帳 because it is an action to
            take now, not a number to read. */}
        {restock.length > 0 && (
          <Alert severity="warning" icon={<Inventory2OutlinedIcon />} sx={{ mb: 2, textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              記得補貨
            </Typography>
            {restock.map((r) => (
              <Box
                key={r.variant_id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {r.product_title || r.sku}
                  {r.color ? ` — ${r.color}` : ''}
                  {r.size ? ` / ${r.size}` : ''}：還有 <strong>{r.qty_on_hand}</strong> 件
                  {r.storage_slot ? (
                    <>
                      {' '}
                      在 <strong>{r.storage_slot}</strong>
                    </>
                  ) : (
                    <Typography component="span" variant="body2" color="text.secondary">
                      （未設櫃位）
                    </Typography>
                  )}
                </Typography>
                {/* One tap to clear the 未上架 mark the sale set. Without this the
                    only way back on-shelf is the 庫存 tab on the other page, which
                    nobody walks to mid-shift — and the flag would read 未上架 for
                    every variant that ever sold. */}
                <Button
                  size="small"
                  variant={restocked.has(r.variant_id) ? 'text' : 'outlined'}
                  color="inherit"
                  disabled={restocked.has(r.variant_id) || restockBusy === r.variant_id}
                  onClick={() => markRestocked(r.variant_id)}
                  sx={{ flexShrink: 0 }}
                >
                  {restocked.has(r.variant_id) ? '已補上' : '補好了'}
                </Button>
              </Box>
            ))}
          </Alert>
        )}
        <Paper sx={{ p: 2, mb: 3, display: 'inline-block' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            品牌分帳
          </Typography>
          {Object.entries(bucketVendorTotals(completed.vendor_totals)).map(([bucket, amt]) => (
            <Typography key={bucket} variant="body1">
              {bucket}: <strong>{formatNT(amt)}</strong>
            </Typography>
          ))}
        </Paper>
        {completed.customer_name && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            會員：{completed.customer_name}
          </Typography>
        )}
      <Box>
        <Button variant="contained" size="large" onClick={resetTicket} fullWidth>
          繼續下一筆
        </Button>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          只清空這筆銷售明細，「試衣」的顧客卡與合身清單會保留。
        </Typography>
      </Box>
    </Box>
  ) : null;

  const ticketCount = lines.reduce((s, l) => s + l.qty, 0);

  // ---- Main screen --------------------------------------------------------

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', pb: tab === 'checkout' && !completed ? 12 : 3 }}>
      <Typography variant="h5" gutterBottom>
        NOKE 專櫃
      </Typography>

      {/* 試衣 and 收銀 are separate moments with separate lifecycles — see the
          file header. Switching tabs never clears anything on either side. */}
      <Tabs
        value={tab}
        onChange={(_e, v: PosTab) => setTab(v)}
        variant="fullWidth"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="fitting"
          icon={
            <Badge color="success" variant="dot" invisible={!sessionId}>
              <CheckroomIcon />
            </Badge>
          }
          iconPosition="start"
          label="試衣"
          sx={{ minHeight: 56, fontSize: '1rem' }}
        />
        <Tab
          value="checkout"
          icon={
            <Badge color="primary" badgeContent={ticketCount} max={99}>
              <PointOfSaleIcon />
            </Badge>
          }
          iconPosition="start"
          label={
            parked.length > 0 ? (
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                收銀
                <Typography component="span" variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                  掛單 {parked.length}
                </Typography>
              </Box>
            ) : (
              '收銀'
            )
          }
          sx={{ minHeight: 56, fontSize: '1rem' }}
        />
        <Tab
          value="manual"
          icon={<MenuBookIcon />}
          iconPosition="start"
          label="手冊"
          sx={{ minHeight: 56, fontSize: '1rem' }}
        />
      </Tabs>

      {/* ================= 手冊 (mall POS + counter rules lookup) =============
          Mounted lazily and kept mounted, so returning to a card or a search
          does not refetch the index. Independent of both other tabs. */}
      {mountedManual && (
        <Box sx={{ display: tab === 'manual' ? 'block' : 'none' }}>
          <NokeManualTab />
        </Box>
      )}

      {/* Scanner — rendered ONCE, above the tab panels, and never unmounted.
          The camera holds a live <video> inside SCANNER_ELEMENT_ID; putting this
          block inside a tab panel would tear that element out from under
          Html5Qrcode on every tab switch. Both scanners work on both tabs; only
          what a scan DOES changes (see doLookup). */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          // 手冊 has nothing to scan. Hidden, never unmounted — and left visible
          // if the camera happens to be running, so Html5Qrcode keeps its
          // <video> element (see the comment above).
          display: tab === 'manual' && !scanning ? 'none' : 'block',
        }}
      >
        <Box
          id={SCANNER_ELEMENT_ID}
          sx={{
            width: '100%',
            minHeight: scanning ? 220 : 0,
            mb: scanning ? 2 : 0,
            '& video': { width: '100% !important', borderRadius: 1 },
          }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {tab === 'fitting'
            ? '試衣模式：掃一次吊牌＝記錄一件試穿（不會加入結帳）。相機、掃描槍、手動輸入都可以。'
            :'支援實體掃描槍 (USB / 藍牙)：對準標籤直接掃描即可加入商品，無需點選任何欄位。'}
        </Typography>
        {!scanning ? (
          <Button variant="outlined" fullWidth startIcon={<CameraAltIcon />} onClick={startScanner}>
            開啟相機掃描
          </Button>
        ) : (
          <Button variant="outlined" color="inherit" fullWidth startIcon={<StopCircleIcon />} onClick={stopScanner}>
            停止相機掃描
          </Button>
        )}

        {/* Manual entry — always available even if the camera fails */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="手動輸入條碼 / SKU"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                doLookup(manualCode, false);
                setManualCode('');
              }
            }}
          />
          <Button
            variant="contained"
            onClick={() => {
              doLookup(manualCode, false);
              setManualCode('');
            }}
            disabled={looking || !manualCode.trim()}
            startIcon={looking ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          >
            查詢
          </Button>
        </Box>
      </Paper>

      {/* ================= 試衣 (service mode) =================
          Hidden with display:none rather than unmounted, so a 合身清單 the
          staffer scrolled through survives a trip to 收銀 and back. */}
      <Box sx={{ display: tab === 'fitting' ? 'block' : 'none' }}>

      {/* 試穿計數 — the tab's main job: scan = one 試穿 (see TryonPanel). */}
      <TryonPanel
        ref={tryonRef}
        active={tab === 'fitting'}
        onScanned={feedbackBeep}
        onOpenCard={(item) =>
          openProductCardRef.current?.(
            item.variant_id,
            { title: item.product_title, color: item.color, size: item.size },
            item
          )
        }
      />

      {/* 量身找尺寸 — the previous 試衣 flow, folded away. It logged no 試穿 in
          its first month, so it no longer leads the tab, but a staffer helping
          someone pick a size can still open it. State is untouched by folding. */}
      <Button
        fullWidth
        color="inherit"
        onClick={() => setSizingOpen((v) => !v)}
        endIcon={sizingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ justifyContent: 'space-between', color: 'text.secondary', mb: 1 }}
      >
        量身找尺寸（選填）{sessionId ? ' · 顧客卡已建立' : ''}
      </Button>
      <Collapse in={sizingOpen}>

      {/* 顧客卡 — the single body/member context for the customer being served.
          Every feature on this tab reads it; there is no second body-entry form,
          and NOTHING on the 收銀 tab may clear it. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            顧客卡
            {sessionId && (
              <Chip size="small" color="success" label="已建立" sx={{ ml: 1, height: 20 }} />
            )}
          </Typography>
          {!cardOpen && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {bodyReady
                ? `${heightCm}cm / ${weightKg}kg / ${BUILD_OPTIONS.find((b) => b.code === build)?.label}`
                : '尚未填寫'}
            </Typography>
          )}
          {/* The ONLY control that clears body / session / 合身清單. Completing a
              sale does not, and must not — see resetTicket. */}
          <Button
            size="small"
            color="inherit"
            startIcon={<RestartAltIcon fontSize="small" />}
            disabled={!hasCustomerContext}
            onClick={() => setSwitchCustomerOpen(true)}
            sx={{ flexShrink: 0, color: 'text.secondary' }}
          >
            換顧客
          </Button>
          <IconButton size="small" onClick={() => setCardOpen((v) => !v)} aria-label="展開顧客卡">
            {cardOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={cardOpen}>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="顧客手機（選填，存尺寸用）"
              type="tel"
              value={customerPhone}
              onChange={(e) => {
                setCustomerPhone(e.target.value);
                setCustomerLooked(false);
                setCustomer(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && lookupCustomer()}
            />
            <Button
              variant="outlined"
              onClick={lookupCustomer}
              disabled={customerLoading || !customerPhone.trim()}
              startIcon={customerLoading ? <CircularProgress size={18} /> : <PersonSearchIcon />}
            >
              查詢
            </Button>
          </Box>
          {customer && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {customer.name}
              {customer.is_vip && <Chip size="small" color="primary" label="VIP" sx={{ ml: 1 }} />}
              <Typography variant="caption" display="block">
                建立顧客卡後會把這組尺寸存給這位會員。結帳要綁的會員請在「收銀」分頁輸入。
              </Typography>
            </Alert>
          )}
          {customerLooked && !customer && customerPhone.trim() && (
            <Alert severity="info" sx={{ mt: 1 }}>
              查無此會員，仍可建立顧客卡試算尺寸，只是不會存檔。
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="身高 cm"
              type="number"
              inputProps={{ inputMode: 'numeric', min: 0 }}
              value={heightCm}
              onChange={(e) => {
                setHeightCm(e.target.value);
                invalidateSession();
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="體重 kg"
              type="number"
              inputProps={{ inputMode: 'numeric', min: 0 }}
              value={weightKg}
              onChange={(e) => {
                setWeightKg(e.target.value);
                invalidateSession();
              }}
            />
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              體型（請顧客自己選，只用來推算建議尺寸）
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {BUILD_OPTIONS.map((opt) => (
                <Chip
                  key={opt.code}
                  label={opt.label}
                  color={build === opt.code ? 'primary' : 'default'}
                  variant={build === opt.code ? 'filled' : 'outlined'}
                  onClick={() => {
                    setBuild(opt.code);
                    invalidateSession();
                  }}
                  sx={{ minHeight: 40, px: 1, fontSize: '1rem' }}
                />
              ))}
            </Box>
          </Box>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 1.5 }}
            onClick={saveSession}
            disabled={sessionSaving || !bodyReady}
            startIcon={sessionSaving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {sessionId ? '更新顧客卡' : '建立顧客卡'}
          </Button>

          {sessionError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {sessionError}
            </Alert>
          )}
          {profileSaved && (
            <Alert severity="success" sx={{ mt: 1 }} icon={false}>
              <Typography variant="caption">
                已為您存檔，日後線上購物會依這組尺寸推薦。
              </Typography>
            </Alert>
          )}
          {sessionBody && (
            <Box sx={{ mt: 1.5 }}>
              <BodyRow body={sessionBody} />
            </Box>
          )}
        </Collapse>

        <Button
          variant="outlined"
          fullWidth
          size="large"
          sx={{ mt: 1.5 }}
          onClick={runFitScan}
          disabled={fitLoading}
          startIcon={fitLoading ? <CircularProgress size={18} /> : <CheckroomIcon />}
        >
          {fitLoading ? '搜尋中…' : '找適合的'}
        </Button>
      </Paper>

      {/* 店內合身清單 */}
      {(fitError || fitItems !== null) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            店內合身清單
          </Typography>
          {fitError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {fitError}
            </Alert>
          )}
          {fitBody && (
            <Box sx={{ mb: 1.5 }}>
              <BodyRow body={fitBody} />
            </Box>
          )}
          {fitItems !== null && fitItems.length === 0 && !fitError && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {fitUnmeasured.length > 0
                ? '目前沒有算得出建議尺寸的商品，請看下方「尚未建檔尺寸」。'
                : '目前店內沒有符合的尺寸，可改用「商品資訊」查看尺寸表。'}
            </Typography>
          )}
          {/* Never-empty guarantee: the fit filter can reject every product, and
              a blank list reads as "this store has nothing for this customer".
              Show the closest few with an honest caption instead. */}
          {fitView.fallback && (
            <Alert severity="info" icon={false} sx={{ mb: 1 }}>
              <Typography variant="caption">
                目前店內沒有很合身的選擇。以下是最接近的 {fitView.groups.length} 件，請顧客實際試穿再決定。
              </Typography>
            </Alert>
          )}
          {fitView.groups.map((group) => renderProductGroup(group, 'fit'))}
          {/* Say what was filtered out — silence here looks like a short rail. */}
          {fitView.hiddenProducts > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              已隱藏 {fitView.hiddenProducts} 件明顯不合身的商品（過大、過緊或過長）。
            </Typography>
          )}

          {/* 尚未建檔尺寸 — real garments on the rail we could not score. Hidden
              by default so they never push the scored list down, but one tap
              away: "we haven't measured it" and "it doesn't fit you" call for
              opposite reactions, and staff can simply hand one over to try on. */}
          {fitUnmeasured.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button
                fullWidth
                size="small"
                color="inherit"
                onClick={() => setUnmeasuredOpen((v) => !v)}
                endIcon={unmeasuredOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: 'space-between', color: 'text.secondary' }}
              >
                另有 {fitUnmeasured.length} 件尚未建檔尺寸
              </Button>
              <Collapse in={unmeasuredOpen}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  這些是店內實際有的服飾，只是還沒有尺寸資料，無法算建議尺寸——可以直接拿給顧客試穿。
                </Typography>
                {unmeasuredGroups.map((group) => renderProductGroup(group, 'unmeasured'))}
              </Collapse>
            </Box>
          )}

          {/* Fallback for a backend that has not shipped `unmeasured` yet — the
              list would silently look exhaustive otherwise. */}
          {fitUnmeasured.length === 0 && fitSkipped > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              另有 {fitSkipped} 件無尺寸資料未列入，清單不代表店內全部商品。
            </Typography>
          )}
          {fitNotGarment > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              （不含香氛、保養等非服飾商品）
            </Typography>
          )}
        </Paper>
      )}

      </Collapse>

      </Box>

      {/* ================= 收銀 (checkout) =================
          Also display:none rather than unmounted — an in-progress ticket, its
          discount and its server quote must be exactly where the staffer left
          them after a detour through 試衣. */}
      <Box sx={{ display: tab === 'checkout' ? 'block' : 'none' }}>

      {completedView}

      {/* 掛單中 — carts waiting on a customer who went back to try more on.
          Shown above the live ticket (and under a finished sale) so a waiting
          cart is never out of sight while someone else is being rung up. */}
      {parked.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <PauseCircleOutlineIcon fontSize="small" color="warning" /> 掛單中（{parked.length}）
          </Typography>
          {parked.map((t, i) => (
            <Box
              key={t.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                borderTop: i === 0 ? 'none' : '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* The total gets its own slot: a truncated NT$ is the one
                    thing staff use to tell two carts apart at a glance. */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                    {parkedTicketLabel(t, i)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                    {formatNT(t.total)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {parkedTimeLabel(t.parked_at)}・{parkedTicketSummary(t)}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="warning"
                startIcon={<ReplayIcon fontSize="small" />}
                onClick={() => resumeTicket(t)}
                disabled={submitting}
                sx={{ flexShrink: 0 }}
              >
                叫回
              </Button>
              <IconButton size="small" aria-label="刪除掛單" onClick={() => setDiscardParked(t)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Paper>
      )}

      {!completed && (
      <>
      {/* Running activities — so staff can answer 「為什麼是這個價？」 on the spot.
          Discounts apply automatically; 以最優惠的一項計算, never stacked. */}
      {activeCampaigns.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'success.50' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalOfferIcon fontSize="inherit" /> 今日活動（自動套用，同一件商品以最優惠的一項計算）
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {activeCampaigns.map((c) => (
              <Chip key={c.id} size="small" color="success" variant="outlined" label={c.name} />
            ))}
          </Box>
        </Paper>
      )}

      {/* 結帳會員 — the person PAYING. Deliberately not the 顧客卡 phone: the
          customer in the fitting room is often not the customer at the till, and
          a shared field would stamp the wrong phone onto the sale. Cleared with
          the ticket, never with the 顧客卡. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          結帳會員（選填）
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="結帳人手機"
            type="tel"
            value={ticketPhone}
            onChange={(e) => {
              setTicketPhone(e.target.value);
              setTicketCustomerLooked(false);
              setTicketCustomer(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && lookupTicketCustomer()}
          />
          <Button
            variant="outlined"
            onClick={lookupTicketCustomer}
            disabled={ticketCustomerLoading || !ticketPhone.trim()}
            startIcon={ticketCustomerLoading ? <CircularProgress size={18} /> : <PersonSearchIcon />}
          >
            查詢
          </Button>
        </Box>
        {/* One tap when the two really are the same person. */}
        {!!customerPhone.trim() && customerPhone.trim() !== ticketPhone.trim() && (
          <Button
            size="small"
            sx={{ mt: 1 }}
            startIcon={<PersonSearchIcon fontSize="small" />}
            onClick={useFittingCustomerOnTicket}
          >
            帶入試衣顧客 {customer ? `${customer.name} ` : ''}{customerPhone.trim()}
          </Button>
        )}
        {ticketCustomer && (
          <Alert severity="success" sx={{ mt: 1 }}>
            {ticketCustomer.name}
            {ticketCustomer.is_vip && <Chip size="small" color="primary" label="VIP" sx={{ ml: 1 }} />}
            <Typography variant="caption" display="block">
              將綁定此會員（專櫃不提供會員折扣）
            </Typography>
          </Alert>
        )}
        {ticketCustomerLooked && !ticketCustomer && ticketPhone.trim() && (
          <Alert severity="info" sx={{ mt: 1 }}>
            查無此會員，將僅以電話 {ticketPhone.trim()} 記錄於此筆銷售。
          </Alert>
        )}
      </Paper>

      {/* Ticket lines */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          銷售明細
        </Typography>
        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            掃描或輸入條碼以加入商品
          </Typography>
        ) : (
          lines.map((l) => {
            const key = lineKey(l.variant_id, l.is_gift);
            const ql = quoteByLine.get(key);
            const lineGross = l.is_gift ? 0 : l.unit_price * l.qty;
            const lineNet = lineGross - (ql?.line_discount || 0);
            const discounted = !!ql && ql.line_discount > 0;
            return (
              <Box key={key} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {l.product_title}
                      {l.is_gift && (
                        <Chip size="small" color="secondary" label="贈品" sx={{ ml: 0.5, height: 18 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {l.color}
                      {l.size ? ` / ${l.size}` : ''} ·{' '}
                      {l.is_gift ? (
                        <>
                          <s>{formatNT(l.unit_price)}</s> <strong>NT$ 0</strong>
                        </>
                      ) : discounted ? (
                        <>
                          <s>{formatNT(lineGross)}</s> <strong>{formatNT(lineNet)}</strong>
                        </>
                      ) : (
                        formatNT(l.unit_price)
                      )}
                      {l.qty_on_hand <= 0 && (
                        <Chip size="small" color="warning" label="在櫃庫存不足" sx={{ ml: 0.5, height: 18 }} />
                      )}
                    </Typography>
                    {((l.qty_office || 0) > 0 || (l.qty_in_transit || 0) > 0) && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        NOKE 在櫃 {l.qty_on_hand} 件 · 辦公室 {l.qty_office || 0} 件 · 運送中 {l.qty_in_transit || 0} 件
                      </Typography>
                    )}
                    {discounted && ql?.campaign_name && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="success"
                        label={ql.campaign_name}
                        sx={{ height: 18, mt: 0.25 }}
                      />
                    )}
                  </Box>
                  <Tooltip title={l.is_gift ? '取消贈品' : '設為贈品 (NT$ 0)'}>
                    <IconButton
                      size="small"
                      color={l.is_gift ? 'secondary' : 'default'}
                      onClick={() => toggleGift(key)}
                    >
                      <CardGiftcardIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => changeQty(key, -1)}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ minWidth: 20, textAlign: 'center' }}>{l.qty}</Typography>
                  <IconButton size="small" onClick={() => changeQty(key, 1)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => removeLine(key)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                {/* Answer + fitting affordances. Second row on purpose: the qty
                    controls above are already at the limit of a thumb's aim. */}
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                    onClick={() =>
                      openProductCard(l.variant_id, {
                        title: l.product_title,
                        color: l.color,
                        size: l.size,
                      })
                    }
                  >
                    商品資訊
                  </Button>
                </Box>
              </Box>
            );
          })
        )}

        {/* 複製商品連結 — staff pastes this into the official LINE account. */}
        {lines.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={copyLinks}
              disabled={linkableLines.length === 0}
            >
              複製商品連結{linkableLines.length > 0 ? `（${linkableLines.length} 件）` : ''}
            </Button>
            {linkableLines.length === 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                這些商品沒有網站連結資料，無法複製。
              </Typography>
            )}
            <Collapse in={copyOpen}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {copyOk
                    ? '已複製到剪貼簿，可直接貼到 LINE。'
                    : '無法自動複製（瀏覽器不允許），請長按下方文字全選後複製。'}
                </Typography>
                {/* Always rendered, not only on failure: clipboard permission in
                    a mall-wifi browser is unreliable, and staff need something
                    they can long-press and select without diagnosing why. */}
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  value={linkText}
                  InputProps={{ readOnly: true }}
                  inputProps={{ style: { fontSize: 12, lineHeight: 1.5 } }}
                  onFocus={(e) => e.target.select()}
                  sx={{ mt: 0.5 }}
                />
                {missingLinkCount > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    另有 {missingLinkCount} 件沒有連結資料，未包含在上面。
                  </Typography>
                )}
                <Button size="small" onClick={() => setCopyOpen(false)} sx={{ mt: 0.5 }}>
                  收起
                </Button>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* 滿額贈 earned on this ticket. The gift itself is rung as a line and
            marked 贈品 with the gift button, so it still comes out of 庫存. */}
        {quote && quote.gifts.length > 0 && (
          <Alert severity="success" icon={<CardGiftcardIcon fontSize="inherit" />} sx={{ mt: 1 }}>
            符合贈品：{quote.gifts.map((g) => g.label).join('、')}
            <Typography variant="caption" display="block">
              請掃描贈品條碼後點「禮物」圖示標記為贈品（金額 0，仍會扣庫存）
            </Typography>
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <TextField
            label="額外折扣 (NT$)"
            type="number"
            size="small"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">-</InputAdornment> }}
            inputProps={{ min: 0 }}
            error={(parseFloat(discount) || 0) > gross || (parseFloat(discount) || 0) < 0}
            helperText={
              (parseFloat(discount) || 0) > gross
                ? `折扣不可超過總額，將以 ${formatNT(gross)} 計算`
                : (parseFloat(discount) || 0) < 0
                  ? '折扣不可為負數，將以 NT$ 0 計算'
                  : '活動折扣已自動計算，這裡只填額外的人工折讓'
            }
            fullWidth
          />
        </Box>
      </Paper>

      {/* Salesperson — tap-to-pick from the 門市 roster. Buttons, not a text
          field or a Select: a focused text input swallows scanner bursts, and a
          Select's type-ahead would let one pick a colleague's name. A focused
          button ignores printable keys, and useBarcodeScanner preventDefaults
          the scan's Enter so it can never "click" the highlighted name. */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          銷售人員
        </Typography>
        {staffFailed || staffOptions.length === 0 ? (
          <TextField
            fullWidth
            size="small"
            label="銷售人員"
            value={salesperson}
            onChange={(e) => setSalesperson(e.target.value)}
            helperText={
              staffFailed
                ? '門市人員名單載入失敗，請直接輸入姓名'
                : '尚無門市人員帳號，請直接輸入姓名'
            }
          />
        ) : (
          <>
            <ToggleButtonGroup
              exclusive
              value={salesperson}
              onChange={(_e, value: string | null) => {
                // null = tapping the already-selected name. Keep it selected:
                // an accidental double-tap must not silently drop attribution.
                if (!value) return;
                salespersonTouchedRef.current = true;
                setSalesperson(value);
                localStorage.setItem(SALESPERSON_STORAGE_KEY, value);
              }}
              size="small"
              sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: 1, borderRadius: 1 } }}
            >
              {staffOptions.map((s) => (
                <ToggleButton key={s.id} value={s.name} sx={{ px: 2 }}>
                  {s.name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {!salesperson && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                請點選本筆的銷售人員
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* Sticky total + complete bar */}
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          zIndex: 1100,
          borderRadius: 0,
        }}
      >
        <Box sx={{ maxWidth: 520, mx: 'auto' }}>
          {/* Breakdown so staff can see WHY the total is what it is — and so a
              customer querying the price gets an answer, not a shrug. */}
          {lines.length > 0 && quote && (quote.campaign_discount > 0 || quote.manual_discount > 0) && (
            <Box sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">原價小計</Typography>
                <Typography variant="caption" color="text.secondary">{formatNT(quote.gross)}</Typography>
              </Box>
              {quote.campaign_discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="success.main">活動折扣</Typography>
                  <Typography variant="caption" color="success.main">
                    -{formatNT(quote.campaign_discount)}
                  </Typography>
                </Box>
              )}
              {quote.manual_discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">額外折扣</Typography>
                  <Typography variant="caption" color="text.secondary">
                    -{formatNT(quote.manual_discount)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="subtitle1">
              應收總額
              {quoting && <CircularProgress size={12} sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, opacity: quoting ? 0.5 : 1 }}>
              {formatNT(netTotal)}
            </Typography>
          </Box>
          {lines.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              {Object.entries(vendorTotals).map(([bucket, amt]) => (
                <Typography key={bucket} variant="body2" color="text.secondary">
                  {bucket}: {formatNT(amt)}
                </Typography>
              ))}
            </Box>
          )}
          {/* 現金找零 — the calculator the staffer would otherwise pick up.
              Arithmetic on the total right above it: no payment is taken here
              and nothing about the cash is recorded on the sale. */}
          {lines.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Button
                size="small"
                startIcon={<PointOfSaleIcon fontSize="small" />}
                endIcon={cashOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setCashOpen((o) => !o)}
                sx={{ textTransform: 'none' }}
              >
                現金找零
                {/* Folded away, the answer still rides on the button — a staffer
                    mid-count never has to reopen anything to read it. */}
                {!cashOpen && changeDue !== null && changeDue >= 0 && (
                  <Typography component="span" color="success.main" sx={{ ml: 1, fontWeight: 700 }}>
                    找 {formatNT(changeDue)}
                  </Typography>
                )}
              </Button>
              <Collapse in={cashOpen}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {cashSuggestions(netTotal).map((v) => (
                    <Chip
                      key={v}
                      size="small"
                      label={v === Math.round(netTotal) ? `剛好 ${formatNT(v)}` : formatNT(v)}
                      onClick={() => setCashReceived(String(v))}
                      color={cashNum === v ? 'primary' : 'default'}
                      variant={cashNum === v ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                <TextField
                  label="收到現金"
                  type="number"
                  size="small"
                  fullWidth
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  inputProps={{ min: 0, inputMode: 'numeric' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">NT$</InputAdornment>,
                    endAdornment:
                      cashReceived !== '' ? (
                        <InputAdornment position="end">
                          <IconButton size="small" aria-label="清除" onClick={() => setCashReceived('')}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                {changeDue !== null &&
                  (changeDue >= 0 ? (
                    // Dimmed while a re-quote is in flight, for the same reason
                    // the total is: a number that is about to move must not be
                    // paid out of the float.
                    <Box sx={{ mt: 1, opacity: quoting ? 0.5 : 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography variant="subtitle1" color="success.main">
                          找零
                        </Typography>
                        <Typography variant="h4" color="success.main" sx={{ fontWeight: 800 }}>
                          {formatNT(changeDue)}
                        </Typography>
                      </Box>
                      {changeDue > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {changeBreakdown(changeDue)
                            .map((d) => `${d.denom} × ${d.count}`)
                            .join('　')}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                      還差 {formatNT(-changeDue)}
                    </Alert>
                  ))}
              </Collapse>
            </Box>
          )}
          {/* Never let staff key a number we could not verify into the mall POS. */}
          {quoteError && (
            <Alert severity="error" sx={{ mb: 1, py: 0 }}>
              {quoteError}
            </Alert>
          )}
          <Divider sx={{ mb: 1 }} />
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleComplete}
            disabled={submitting || lines.length === 0 || quoting || !!quoteError || !quote}
            startIcon={submitting ? <CircularProgress size={22} color="inherit" /> : null}
          >
            {submitting ? '處理中…' : quoting ? '計算中…' : '完成銷售'}
          </Button>
          {lines.length > 0 && (
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              sx={{ mt: 1 }}
              startIcon={<PauseCircleOutlineIcon />}
              onClick={() => setParkOpen(true)}
              disabled={submitting}
            >
              掛單（客人還要試穿，先結下一位）
            </Button>
          )}
        </Box>
      </Paper>

      </>
      )}

      </Box>
      {/* ================= end 收銀 ================= */}

      {/* 商品答案卡 — the answers to what a customer asks across the counter.
          Shared by both tabs: on 收銀 it is opened from a ticket line, on 試衣
          from a scan or a 合身清單 row. */}
      <Drawer
        anchor="bottom"
        open={sheetVariantId !== null}
        onClose={closeProductCard}
        PaperProps={{ sx: { maxHeight: '85vh', borderTopLeftRadius: 12, borderTopRightRadius: 12 } }}
      >
        <Box sx={{ p: 2, maxWidth: 520, mx: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 700 }}>
              {orDash(sheetCard?.title || sheetFallback?.title)}
            </Typography>
            <IconButton size="small" onClick={closeProductCard} aria-label="關閉">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* 加入結帳 — rendered BEFORE the card body and outside every loading /
              error branch on purpose. A 試衣 confirmation's 商品資訊 lands here, and the
              customer saying 「這件我要」 must never be blocked by a size chart
              that is still loading or by a backend that is down. */}
          {sheetAddable && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddShoppingCartIcon />}
                onClick={addSheetItemToTicket}
                sx={{ flexGrow: 1 }}
              >
                加入結帳 {formatNT(sheetAddable.price)}
              </Button>
            </Box>
          )}

          {sheetLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {/* Backend unreachable: keep what we already know from the ticket line
              on screen rather than dropping to a blank sheet mid-sale. */}
          {sheetError && !sheetLoading && (
            <>
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() =>
                      sheetVariantId !== null &&
                      openProductCard(
                        sheetVariantId,
                        sheetFallback || { title: '', color: '', size: '' },
                        // Keep 加入結帳 alive across a retry.
                        sheetAddable
                      )
                    }
                  >
                    重試
                  </Button>
                }
              >
                {sheetError}
              </Alert>
              {sheetFallback && (
                <Typography variant="body2" color="text.secondary">
                  {orDash(sheetFallback.color)} / {orDash(sheetFallback.size)}
                </Typography>
              )}
            </>
          )}

          {!sheetLoading && !sheetError && sheetCard && (
            <>
              {sessionId && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    建議尺寸
                  </Typography>
                  <Chip
                    color={sheetCard.recommended_size ? 'primary' : 'default'}
                    label={orDash(sheetCard.recommended_size)}
                    sx={{ fontSize: '1rem', height: 34 }}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {orDash(sheetCard.color)} / {orDash(sheetCard.size)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sheetCard.price === null || sheetCard.price === undefined
                    ? '—'
                    : formatNT(sheetCard.price)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  SKU {orDash(sheetCard.sku)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="text.secondary" display="block">
                材質
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
                {orDash(sheetCard.fabric)}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block">
                尺寸表（平量單邊寬度，非圍度）
              </Typography>
              {sheetCard.size_chart && sheetCard.size_chart.length > 0 ? (
                <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>尺寸</TableCell>
                        {chartColumns(sheetCard.size_chart).map((k) => (
                          <TableCell key={k} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {chartColumnLabel(k)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sheetCard.size_chart.map((row, i) => (
                        <TableRow
                          key={`${row.size_label}-${i}`}
                          selected={
                            !!sheetCard.recommended_size && row.size_label === sheetCard.recommended_size
                          }
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{orDash(row.size_label)}</TableCell>
                          {chartColumns(sheetCard.size_chart!).map((k) => (
                            <TableCell key={k} align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {orDash(row[k] as string | number | null)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  —
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary" display="block">
                模特兒著用
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {orDash(sheetCard.model_spec)}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block">
                產地
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {orDash(sheetCard.origin)}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block">
                洗滌
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
                {orDash(sheetCard.care)}
              </Typography>

              {sheetCard.handle && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={async () => {
                    const text = `${orDash(sheetCard.title)} ${orDash(sheetCard.color)}${
                      sheetCard.size ? ` ${sheetCard.size}` : ''
                    }\n${pdpUrl(sheetCard.handle as string)}`;
                    try {
                      if (navigator.clipboard && window.isSecureContext) {
                        await navigator.clipboard.writeText(text);
                        enqueueSnackbar('已複製商品連結', { variant: 'success' });
                        return;
                      }
                    } catch {
                      /* fall through to the ticket's copy block */
                    }
                    enqueueSnackbar('無法自動複製，請用明細下方的「複製商品連結」', {
                      variant: 'warning',
                    });
                  }}
                >
                  複製這件的連結
                </Button>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* 換顧客 — the single deliberate exit from a customer's context. It asks
          first because the numbers on that card were said out loud by a person
          standing at the counter, and it says plainly that the ticket is safe. */}
      <Dialog open={switchCustomerOpen} onClose={() => setSwitchCustomerOpen(false)}>
        <DialogTitle>換顧客？</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            會清空顧客卡的身高、體重、體型與店內合身清單。
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              「收銀」分頁未結帳的明細
              {lines.length > 0 ? `（${lines.length} 項）` : ''}
              不會受影響。
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwitchCustomerOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              resetCustomer();
              setSwitchCustomerOpen(false);
              setTab('fitting');
            }}
          >
            換顧客
          </Button>
        </DialogActions>
      </Dialog>

      {/* 掛單 — one optional note so staff can tell two waiting carts apart
          (「黑外套先生」). Blank is fine: the 會員/phone or 掛單 N labels it. */}
      <Dialog
        open={parkOpen}
        onClose={() => {
          setParkOpen(false);
          setParkNote('');
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>掛單</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            這筆明細（{lines.reduce((s, l) => s + l.qty, 0)} 件・{formatNT(netTotal)}）會先存起來，客人回來時按「叫回」繼續結帳。
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="備註（選填，例如：黑外套先生）"
            value={parkNote}
            onChange={(e) => setParkNote(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && parkTicket()}
            inputProps={{ maxLength: 30 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setParkOpen(false);
              setParkNote('');
            }}
          >
            取消
          </Button>
          <Button variant="contained" color="warning" onClick={parkTicket}>
            掛單
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!discardParked} onClose={() => setDiscardParked(null)}>
        <DialogTitle>刪除這筆掛單？</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {discardParked &&
              `${parkedTicketLabel(discardParked, parked.indexOf(discardParked))}：${parkedTicketSummary(discardParked)}・${formatNT(discardParked.total)}`}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} component="span" display="block">
              只在客人確定不買時刪除；不會影響庫存。
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardParked(null)}>取消</Button>
          <Button variant="contained" color="error" onClick={() => discardParked && deleteParked(discardParked.id)}>
            刪除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NokePosPage;
